import { ReactElement } from "react";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { GeneralFeedItemModel, ActivityFeedItemKind } from "./flattened";
import { AnimatedStack } from "./Stack/AnimatedStack";
import { ExpandedStackFooterActivityFeedItem } from "./Stack/ExpandedStackFooterActivityFeedItem";
import { AvailableNotificationViewModel } from "client/Api/Notifications/notifications";
import { ActivityStackState } from "./stacks";
import { Activity } from "./Activity/Activity";

export type ActivityFeedItemProps = {
	activityFeedItem: GeneralFeedItemModel;
	onDeleteActivity(activity: AvailableNotificationViewModel): void;
	onDeleteStack(stackId: string): void;
	onMuteActivity(activity: AvailableNotificationViewModel): void;
	onMuteStack(stackId: string): void;
	onScrollToItem(item: GeneralFeedItemModel): void;
	onStackStateChanged(stackId: string, state: ActivityStackState): void;
	onActivityChanged: () => void;
	onActivityInvoked?: () => void;
};

export const ActivityFeedItem = (props: ActivityFeedItemProps): ReactElement => {
	const {
		activityFeedItem,
		onDeleteActivity,
		onDeleteStack,
		onScrollToItem,
		onStackStateChanged,
		onMuteActivity,
		onMuteStack,
		onActivityChanged,
		onActivityInvoked
	} = props;

	switch (activityFeedItem.kind) {
		case ActivityFeedItemKind.Activity:
			return (
				<Activity
					categoryName=""
					activity={activityFeedItem.activity}
					onDelete={() => onDeleteActivity(activityFeedItem.activity)}
					onMute={() => onMuteActivity(activityFeedItem.activity)}
					onActivityChanged={onActivityChanged}
					onActivityInvoked={onActivityInvoked}
				/>
			);
		case ActivityFeedItemKind.AnimatedStack:
			return (
				<AnimatedStack
					stackId={activityFeedItem.stackId}
					state={activityFeedItem.isStackExpanded ? "expanded" : "collapsed"}
					title={activityFeedItem.title}
					activities={activityFeedItem.activities}
					footerKind={activityFeedItem.footerKind}
					totalNumberOfActivities={activityFeedItem.totalNumberOfActivities}
					hasReducedPaddingAfterExpansion={activityFeedItem.hasReducedPaddingAfterExpansion}
					canBeDeletedByUser={activityFeedItem.canBeDeletedByUser}
					onCollapse={() => onStackStateChanged(activityFeedItem.stackId, "collapsed")}
					onCollapseFromFooter={() => {
						onScrollToItem(activityFeedItem);
						onStackStateChanged(activityFeedItem.stackId, "collapsed");
					}}
					onExpanded={() => onStackStateChanged(activityFeedItem.stackId, "expanded")}
					onExpansionStarted={() => onStackStateChanged(activityFeedItem.stackId, "expanding")}
					onDelete={() => onDeleteStack(activityFeedItem.stackId)}
					onDeleteActivity={onDeleteActivity}
					onMute={() => onMuteStack(activityFeedItem.stackId)}
					onMuteActivity={onMuteActivity}
					onActivityChanged={onActivityChanged}
					onActivityInvoked={onActivityInvoked}
				/>
			);
		case ActivityFeedItemKind.ExpandedStackFooter:
			return (
				<ExpandedStackFooterActivityFeedItem
					stackId={activityFeedItem.stackId}
					shouldRenderButtons={activityFeedItem.shouldRenderButtons}
					canStackBeDeletedByUser={activityFeedItem.canBeDeletedByUser}
					onCollapse={() => {
						onScrollToItem(activityFeedItem.animatedItem);
						onStackStateChanged(activityFeedItem.stackId, "collapsed");
					}}
					onDelete={() => onDeleteStack(activityFeedItem.stackId)}
					onMute={() => onMuteStack(activityFeedItem.stackId)}
				/>
			);
		default:
			throw new UnreachableCaseError(activityFeedItem);
	}
};

ActivityFeedItem.displayName = "ActivityFeedItem";